import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/extends";
const _excluded = ["biViewMode", "reportGatePageLoadEvents", "useReferralAdditionalInfoAsSearchOrigin", "querySubmitPath", "hideSearchBarShadowOnPartialMatch", "showSearchIcon", "hideFilterChips", "useCompactRecommendationsLayout", "deviceType", "productType", "voucherPlacement"];
import { Box } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import clsx from 'clsx';
import { WIX_HEADER_HEIGHT } from '@wix/core-domain-search';
import React from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useIsScrolled } from '../../hooks/useIsScrolled';
import s from '../anonymousResultsPage.scss';
import transition from '../viewTransitions.css';
import { ResultsPageProvider, useResultsPageContext } from './ResultsPageContext';
import { VoucherRow } from './VoucherRow';
const ResultsLayoutShell = _ref => {
  var _recommendations$;
  let _ref$showCartHeader = _ref.showCartHeader,
    showCartHeader = _ref$showCartHeader === void 0 ? false : _ref$showCartHeader,
    _ref$stickyHeaderOffs = _ref.stickyHeaderOffset,
    stickyHeaderOffset = _ref$stickyHeaderOffs === void 0 ? WIX_HEADER_HEIGHT : _ref$stickyHeaderOffs,
    headerPadding = _ref.headerPadding,
    headerTop = _ref.headerTop,
    headerMargin = _ref.headerMargin,
    headerGap = _ref.headerGap,
    _ref$desktopGridGap = _ref.desktopGridGap,
    desktopGridGap = _ref$desktopGridGap === void 0 ? 'SP4 0' : _ref$desktopGridGap,
    _ref$searchBarFullWid = _ref.searchBarFullWidth,
    searchBarFullWidth = _ref$searchBarFullWid === void 0 ? false : _ref$searchBarFullWid,
    _ref$optimizedSearchB = _ref.optimizedSearchBarStyle,
    optimizedSearchBarStyle = _ref$optimizedSearchB === void 0 ? false : _ref$optimizedSearchB,
    searchBar = _ref.searchBar,
    header = _ref.header,
    voucher = _ref.voucher,
    _ref$voucherPlacement = _ref.voucherPlacement,
    voucherPlacement = _ref$voucherPlacement === void 0 ? 'stickyHeader' : _ref$voucherPlacement,
    voucherMaxWidth = _ref.voucherMaxWidth,
    _ref$mobileVoucherInS = _ref.mobileVoucherInSearchBar,
    mobileVoucherInSearchBar = _ref$mobileVoucherInS === void 0 ? false : _ref$mobileVoucherInS,
    footer = _ref.footer,
    mobileBackButton = _ref.mobileBackButton,
    dataHook = _ref.dataHook,
    children = _ref.children;
  const isMobile = useIsMobile();
  const isScrolled = useIsScrolled();
  const _useResultsPageContex = useResultsPageContext(),
    recommendations = _useResultsPageContex.recommendations,
    error = _useResultsPageContex.error,
    hideSearchBarShadowOnPartialMatch = _useResultsPageContex.hideSearchBarShadowOnPartialMatch;
  const showsVoucherOutsideStickyHeader = !isMobile && voucherPlacement === 'outsideStickyHeader' && Boolean(voucher);
  const showsVoucherInMobileSearchBar = isMobile && mobileVoucherInSearchBar && Boolean(voucher);
  const showsVoucherInStickyHeader = Boolean(voucher) && !showsVoucherOutsideStickyHeader && !showsVoucherInMobileSearchBar;
  const removeSearchBarShadow = hideSearchBarShadowOnPartialMatch && recommendations.length > 0 && ((_recommendations$ = recommendations[0]) == null ? void 0 : _recommendations$.label) !== RecommendationLabel.EXACT_MATCH;
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: dataHook,
    display: "block",
    boxSizing: "border-box",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "grid",
    align: "center",
    className: clsx(s.resultsPage, s.noInsights, showsVoucherOutsideStickyHeader && s.withVoucherRow),
    gap: isMobile ? 0 : desktopGridGap,
    height: "100%",
    backgroundColor: "white",
    overflow: "visible"
  }, showsVoucherOutsideStickyHeader && /*#__PURE__*/React.createElement(VoucherRow, {
    maxWidth: voucherMaxWidth
  }, voucher), /*#__PURE__*/React.createElement(Box, _extends({
    boxSizing: "border-box",
    align: "center",
    gridArea: "header",
    top: headerTop ?? (isMobile ? undefined : '4px'),
    position: isMobile ? undefined : 'sticky',
    width: "100%",
    zIndex: 10,
    padding: headerPadding ?? (isMobile ? '0 SP4' : '0 48px SP2 48px'),
    margin: headerMargin ?? (isMobile ? 'SP4 0 SP1' : undefined),
    backgroundColor: "white",
    direction: "vertical",
    gap: headerGap ?? (isMobile ? 'SP5' : 'SP6')
  }, showCartHeader && {
    paddingTop: 0,
    top: '56px' // height of the cart header
  }), isMobile && mobileBackButton, showsVoucherInStickyHeader && /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    maxWidth: voucherMaxWidth
  }, voucher), header, !isMobile && /*#__PURE__*/React.createElement("div", {
    style: {
      ...(searchBarFullWidth && {
        width: '100%'
      }),
      zIndex: 11
    },
    className: clsx(transition.searchBar, optimizedSearchBarStyle && s.optimizedSearchBar, removeSearchBarShadow && s.noSearchBarShadow)
  }, searchBar)), isMobile && /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    top: stickyHeaderOffset,
    backgroundColor: isScrolled ? 'white' : 'transparent',
    className: s.mobileSearchBar,
    gap: "20px"
  }, showsVoucherInMobileSearchBar && voucher, /*#__PURE__*/React.createElement("div", {
    className: clsx(transition.searchBar, removeSearchBarShadow && s.noSearchBarShadow)
  }, searchBar)), children, !error && recommendations.length > 0 && footer));
};
export const ResultsLayout = _ref2 => {
  let biViewMode = _ref2.biViewMode,
    reportGatePageLoadEvents = _ref2.reportGatePageLoadEvents,
    useReferralAdditionalInfoAsSearchOrigin = _ref2.useReferralAdditionalInfoAsSearchOrigin,
    querySubmitPath = _ref2.querySubmitPath,
    hideSearchBarShadowOnPartialMatch = _ref2.hideSearchBarShadowOnPartialMatch,
    showSearchIcon = _ref2.showSearchIcon,
    hideFilterChips = _ref2.hideFilterChips,
    useCompactRecommendationsLayout = _ref2.useCompactRecommendationsLayout,
    deviceType = _ref2.deviceType,
    productType = _ref2.productType,
    _ref2$voucherPlacemen = _ref2.voucherPlacement,
    voucherPlacement = _ref2$voucherPlacemen === void 0 ? 'stickyHeader' : _ref2$voucherPlacemen,
    shellProps = _objectWithoutPropertiesLoose(_ref2, _excluded);
  return /*#__PURE__*/React.createElement(ResultsPageProvider, {
    biViewMode: biViewMode,
    reportGatePageLoadEvents: reportGatePageLoadEvents,
    useReferralAdditionalInfoAsSearchOrigin: useReferralAdditionalInfoAsSearchOrigin,
    querySubmitPath: querySubmitPath,
    hideSearchBarShadowOnPartialMatch: hideSearchBarShadowOnPartialMatch,
    showSearchIcon: showSearchIcon,
    hideFilterChips: hideFilterChips,
    useCompactRecommendationsLayout: useCompactRecommendationsLayout,
    deviceType: deviceType,
    productType: productType
  }, /*#__PURE__*/React.createElement(ResultsLayoutShell, _extends({}, shellProps, {
    voucher: shellProps.voucher,
    voucherPlacement: voucherPlacement
  })));
};
//# sourceMappingURL=ResultsLayout.js.map